import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from 'antd';
import { CHAT_UI_THEME_TOKENS, DEFAULT_CHAT_UI_THEME } from '../theme/themeTokens.js';
import { buildRoundEntries } from './components/timelineRounds.js';
import { AssistantErrorCard } from './components/AssistantErrorCard.jsx';
import { renderTimelineItemNode } from './components/timelineItemRenderer.jsx';
import { buildTimelineElements } from './components/timelineElements.jsx';
import { isThinkingFailed, shouldKeepThinkingVisible } from './components/thinkingState.js';
import { isRegenerableTimelineItem } from './components/timelineRetryPolicy.js';
import { shouldPreloadOlderHistory } from './components/historyPreloadPolicy.js';

export { shouldKeepThinkingVisible };

const conditionalValue = (condition, whenTrue, whenFalse) => (
  condition ? whenTrue : whenFalse
);

const normalizeOlderWindowState = (messageWindow) => {
  const source = Object.assign({}, messageWindow);
  return {
    hasOlder: [source.hasOlder === true, source.has_older === true].some(Boolean),
    loadingOlder: source.loadingOlder === true,
    error: source.error || null,
  };
};

/**
 * DOC HELPER — ChatTimeline projection boundary.
 *
 * Responsibility:
 * - Render timeline entries already produced by chat-core/backend canonical
 *   state.
 * - Group entries into display rounds and delegate each item to
 *   `renderTimelineItemNode`.
 *
 * Inputs:
 * - items: Array of timeline entries. Expected entries include message,
 *   streaming, thinking, orchestration_status, ui_cards, and other renderer
 *   supported types.
 * - isEmpty: Whether to render the configured empty state.
 * - loading: Whether the current assistant round is still streaming/running.
 * - error/onRetry: Optional retry/regenerate display and callback.
 * - registry/onUICardAction: Generative UI renderer registry and action
 *   callback for backend-provided UI cards.
 * - themeTokens/contentMaxWidth: Display-only style inputs.
 * - assistantName: Host-resolved instance name shown beside assistant mode icons.
 * - renderMessageAvatar: Optional presentation-only avatar replacement.
 * - recommendationPanelNode: Optional host-prepared presentation tail; not a canonical message.
 * - activeModeKey: Local composer mode. Kept as an input for compatibility,
 *   but not used to decide authoritative process visibility.
 *
 * Output:
 * - React timeline node.
 *
 * Boundary:
 * - Backend canonical events decide whether orchestration/process rows exist.
 * - Do not gate `orchestration_status` rendering by local composer mode.
 * - Do not infer workflow state from historical message text in this component.
 */
export default function ChatTimeline(props) {
  const {
    items,
    isEmpty,
    loading,
    error,
    onRetry,
    registry,
    skillProjection,
    onUICardAction,
    onMessageCapabilityAction,
    onWorkflowOperationAction,
    themeTokens,
    contentMaxWidth,
    activeModeKey,
    assistantName,
    latestScrollSignal,
    onLatestUnreadChange,
    messageWindow,
    onLoadOlderMessages,
    renderMessageAvatar,
    recommendationPanelNode,
  } = {
    activeModeKey: '',
    contentMaxWidth: 780,
    error: null,
    isEmpty: false,
    items: [],
    latestScrollSignal: 0,
    loading: false,
    messageWindow: null,
    recommendationPanelNode: null,
    themeTokens: CHAT_UI_THEME_TOKENS[DEFAULT_CHAT_UI_THEME],
    ...props,
  };
  const containerRef = useRef(null);
  const previousElementKeysRef = useRef([]);
  const olderLoadInFlightRef = useRef(false);
  void activeModeKey;
  void isEmpty;

  const setLatestUnread = useCallback((visible) => {
    onLatestUnreadChange?.(Boolean(visible));
  }, [onLatestUnreadChange]);

  const olderWindowState = useMemo(
    () => normalizeOlderWindowState(messageWindow),
    [messageWindow],
  );

  const handleLoadOlder = useCallback(() => {
    const unavailable = [
      !olderWindowState.hasOlder,
      olderWindowState.loadingOlder,
      olderLoadInFlightRef.current,
      typeof onLoadOlderMessages !== 'function',
    ].some(Boolean);
    if (unavailable) {
      return false;
    }
    olderLoadInFlightRef.current = true;
    try {
      return Promise.resolve(onLoadOlderMessages()).finally(() => {
        olderLoadInFlightRef.current = false;
      });
    } catch (loadError) {
      olderLoadInFlightRef.current = false;
      throw loadError;
    }
  }, [olderWindowState.hasOlder, olderWindowState.loadingOlder, onLoadOlderMessages]);

  const latestRegenerableItemId = useMemo(() => {
    const normalizedItems = Array.isArray(items) ? items : [];
    const latestRegenerableItem = [...normalizedItems].reverse().find((entry) => (
      isRegenerableTimelineItem(entry, isThinkingFailed)
    ));
    return String(latestRegenerableItem?.id || '');
  }, [items]);

  const renderItemNode = useCallback((item, hasStreamingContent) => {
    return renderTimelineItemNode({
      item,
      assistantName,
      hasStreamingContent,
      latestRegenerableItemId,
      onRegenerate: onRetry,
      onMessageCapabilityAction,
      onUICardAction,
      onWorkflowOperationAction,
      registry,
      skillProjection,
      renderMessageAvatar,
      themeTokens,
    });
  }, [assistantName, latestRegenerableItemId, onMessageCapabilityAction, onRetry, onUICardAction, onWorkflowOperationAction, registry, renderMessageAvatar, skillProjection, themeTokens]);

  const roundEntries = useMemo(() => buildRoundEntries(items), [items]);

  const elements = useMemo(() => {
    const timelineElements = buildTimelineElements({ roundEntries, renderItemNode, loading });
    if (!recommendationPanelNode) return timelineElements;
    // Measure the tail with the same scroll owner; its key follows the displayed round.
    return [...timelineElements, (
      <React.Fragment key={`${roundEntries.at(-1)?.key ?? ''}-recommendation`}>
        {recommendationPanelNode}
      </React.Fragment>
    )];
  }, [loading, recommendationPanelNode, renderItemNode, roundEntries]);

  const elementKeys = useMemo(() => elements.map((element, index) => (
    String(element.key ?? `timeline-element-${index}`)
  )), [elements]);
  const getItemKey = useCallback(
    (index) => elementKeys[index] ?? `timeline-element-${index}`,
    [elementKeys],
  );
  const virtualizer = useVirtualizer({
    anchorTo: 'end',
    count: elements.length,
    directDomUpdates: true,
    estimateSize: () => 160,
    followOnAppend: true,
    gap: 12,
    getItemKey,
    getScrollElement: () => containerRef.current,
    onChange: (instance) => setLatestUnread(!instance.isAtEnd(96)),
    overscan: 6,
    scrollEndThreshold: 96,
    useAnimationFrameWithResizeObserver: true,
  });
  const virtualItems = virtualizer.getVirtualItems();

  const scrollToLatest = useCallback(() => {
    virtualizer.scrollToEnd();
    setLatestUnread(false);
  }, [setLatestUnread, virtualizer]);

  const handleScroll = useCallback(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }
    setLatestUnread(!virtualizer.isAtEnd(96));
    const shouldLoadOlder = shouldPreloadOlderHistory({
      scrollTop: node.scrollTop,
      viewportHeight: node.clientHeight,
      ...olderWindowState,
    });
    if (shouldLoadOlder) {
      handleLoadOlder();
    }
  }, [handleLoadOlder, olderWindowState, setLatestUnread, virtualizer]);

  useLayoutEffect(() => {
    const previousKeys = previousElementKeysRef.current;
    const currentKeys = new Set(elementKeys);
    const retainedItemExists = previousKeys.some((key) => currentKeys.has(key));
    const shouldStartAtLatest = [
      elementKeys.length > 0,
      previousKeys.length === 0 || !retainedItemExists,
    ].every(Boolean);
    if (!shouldStartAtLatest) {
      previousElementKeysRef.current = elementKeys;
      return undefined;
    }
    const scrollWindow = containerRef.current?.ownerDocument?.defaultView;
    if (!scrollWindow) {
      previousElementKeysRef.current = elementKeys;
      scrollToLatest();
      return undefined;
    }
    const frame = scrollWindow.requestAnimationFrame(() => {
      previousElementKeysRef.current = elementKeys;
      scrollToLatest();
    });
    return () => scrollWindow.cancelAnimationFrame(frame);
  }, [elementKeys, scrollToLatest]);

  useEffect(() => {
    if (!latestScrollSignal) {
      return;
    }
    scrollToLatest();
  }, [latestScrollSignal, scrollToLatest]);

  return (
    <div
      className="flare-chat-timeline"
      ref={containerRef}
      onScroll={handleScroll}
      style={{
        background: themeTokens.appBg,
        flex: 1,
        overflow: 'auto',
        padding: 0,
      }}
    >
      <div
        className="flare-chat-timeline-content"
        style={{
          margin: '0 auto',
          maxWidth: contentMaxWidth,
          width: '100%',
        }}
      >
        {conditionalValue(olderWindowState.hasOlder, (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 12px' }}>
            <Button
              disabled={olderWindowState.loadingOlder}
              loading={olderWindowState.loadingOlder}
              onClick={handleLoadOlder}
              size="small"
              type="text"
            >
              {conditionalValue(olderWindowState.loadingOlder, '正在加载更早消息…', '加载更早消息')}
            </Button>
          </div>
        ), null)}
        {conditionalValue(Boolean(olderWindowState.error), (
          <div style={{ color: themeTokens.danger || '#b42318', fontSize: 12, padding: '0 0 8px', textAlign: 'center' }}>
            {String(olderWindowState.error?.message || olderWindowState.error)}
          </div>
        ), null)}
        <div
          ref={virtualizer.containerRef}
          style={{ position: 'relative', width: '100%' }}
        >
          {virtualItems.map((virtualItem) => (
            <div
              data-index={virtualItem.index}
              key={virtualItem.key}
              ref={virtualizer.measureElement}
              style={{
                left: 0,
                position: 'absolute',
                top: 0,
                width: '100%',
              }}
            >
              {elements[virtualItem.index]}
            </div>
          ))}
        </div>

        <AssistantErrorCard
          error={error}
          loading={loading}
          onRetry={onRetry}
          themeTokens={themeTokens}
        />
      </div>
    </div>
  );
}
